import { useMemo, useState } from 'react';
import { fmtMoney, fmtMoney0, fmtDate, toAUD, TYPE_COLOR } from './b2bLib';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

export default function Customers({ session, customers, fx, onOpenCustomer }) {
  // No finer-grained "b2b_write" flag exists yet — admin is the closest
  // write-permission proxy for this read-only dashboard.
  const canWrite = session?.role === 'admin';
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('name');

  function handleDownload() {
    const columns = [
      { label: 'Name', width: 26, value: r => r.name || '' },
      { label: 'Type', width: 14, value: r => r.type || '' },
      { label: 'Country', width: 14, value: r => r.country || '' },
      { label: 'Currency', width: 10, value: r => r.currency || '' },
      { label: 'Lifetime Spend', width: 16, numFmt: '#,##0.00', value: r => Number(r.lifetime_spend || 0) },
      { label: 'Outstanding', width: 14, numFmt: '#,##0.00', value: r => Number(r.current_outstanding || 0) },
      { label: 'Last Order', width: 14, value: r => r.last_order_date || '' },
      { label: 'Avg Days to Pay', width: 14, value: r => r.avg_days_to_pay ?? '' },
      { label: 'Overdue Count', width: 14, value: r => r.overdue_count ?? 0 },
      { label: 'Pricing Tier', width: 14, value: r => r.pricing_tier || '' },
    ];
    const filename = `FON_B2B_Customers_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'B2B Customers', columns, rows);
    logExportActivity(session, 'B2B Customers', filename);
  }

  const rows = useMemo(() => {
    let r = customers.slice();
    if (q.trim()) { const s = q.trim().toLowerCase(); r = r.filter(c => c.name.toLowerCase().includes(s)); }
    const get = {
      outstanding: c => toAUD(c.current_outstanding, c.currency, fx),
      spend: c => toAUD(c.lifetime_spend, c.currency, fx),
      name: c => c.name.toLowerCase(),
      recent: c => c.last_order_date || '',
    }[sort];
    r.sort((a, b) => { const av = get(a), bv = get(b); if (sort === 'name') return av < bv ? -1 : 1; return av < bv ? 1 : av > bv ? -1 : 0; });
    return r;
  }, [customers, q, sort, fx]);

  const selStyle = { fontSize: 12, padding: '6px 8px', border: '1px solid #e4e4e7', borderRadius: 8, background: '#fff' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customers…" style={{ ...selStyle, minWidth: 220 }} />
        <div style={{ flex: 1 }} />
        {canWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
        <select value={sort} onChange={e => setSort(e.target.value)} style={selStyle}>
          <option value="outstanding">Sort: Outstanding</option>
          <option value="spend">Sort: Lifetime spend</option>
          <option value="recent">Sort: Last order</option>
          <option value="name">Sort: Name</option>
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
        {rows.map(c => {
          const tc = TYPE_COLOR[c.type] || { bg: '#f4f4f5', fg: '#52525b' };
          const contact = Array.isArray(c.contacts) && c.contacts[0] ? c.contacts[0] : null;
          const outstanding = Number(c.current_outstanding || 0);
          return (
            <div key={c.customer_id} onClick={() => onOpenCustomer(c.customer_id)}
              style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'box-shadow .15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a' }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 2 }}>{c.country || '—'} · {c.currency}{c.route_to_market && c.route_to_market !== 'Direct' ? ` · ${c.route_to_market}` : ''}</div>
                </div>
                <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: tc.bg, color: tc.fg }}>{c.type}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <Field label="Lifetime spend" value={fmtMoney0(c.lifetime_spend, c.currency)} />
                <Field label="Outstanding" value={fmtMoney0(outstanding, c.currency)} accent={outstanding > 0 ? '#A56B00' : '#16a34a'} />
                <Field label="Last order" value={fmtDate(c.last_order_date)} />
                <Field label="Avg days to pay" value={c.avg_days_to_pay != null ? `${c.avg_days_to_pay}d` : '—'} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f4f4f5', paddingTop: 8 }}>
                <span style={{ fontSize: 11, color: '#71717a' }}>
                  {contact ? (contact.name || contact.email || contact.phone || 'Contact on file') : 'No contact'}
                </span>
                {c.overdue_count > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: '#dc2626' }}>{c.overdue_count} overdue</span>}
                {c.pricing_tier && <span style={{ fontSize: 10, color: '#a1a1aa' }}>{c.pricing_tier}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, accent }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: accent || '#0a0a0a', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}
